import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  VideoCall as VideoCallIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { useTranslations } from '../../constants/translations/CaseTranslation';

const AppointmentModal = ({ 
  open, 
  onClose, 
  caseId, 
  officeColor, 
  onAppointmentScheduled,
  caseLanguage = 'spanish'
}) => {
  const t = useTranslations(caseLanguage);
  
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [currentAppointment, setCurrentAppointment] = useState(null);
  const [appointmentHistory, setAppointmentHistory] = useState([]);
  const [formData, setFormData] = useState({
    scheduled_date: '',
    meet_link: ''
  });
  const [errors, setErrors] = useState({});

  // Cargar cita vigente y historial cuando se abre el modal
  useEffect(() => {
    if (open && caseId) {
      loadAppointments();
    }
  }, [open, caseId]);

  const loadAppointments = async () => {
    setLoadingHistory(true);
    try {
      // Cargar cita vigente (scheduled y en el futuro)
      const now = new Date().toISOString();
      const { data: currentData, error: currentError } = await supabase
        .from('appointment')
        .select('*')
        .eq('case_id', caseId)
        .eq('status', 'scheduled')
        .gte('scheduled_date', now)
        .order('scheduled_date', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (currentError && currentError.code !== 'PGRST116') {
        console.error('Error loading current appointment:', currentError);
      } else {
        setCurrentAppointment(currentData);
      }

      // Cargar historial completo
      const { data: historyData, error: historyError } = await supabase
        .from('appointment')
        .select('*')
        .eq('case_id', caseId)
        .order('scheduled_date', { ascending: false });

      if (historyError) {
        console.error('Error loading appointment history:', historyError);
      } else {
        setAppointmentHistory(historyData || []);
      }
    } catch (error) {
      console.error('Error loading appointments:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const now = new Date();
    
    // Validar fecha
    if (!formData.scheduled_date) {
      newErrors.scheduled_date = t.dateTimeRequired;
    } else {
      const selectedDate = new Date(formData.scheduled_date);
      if (selectedDate <= now) {
        newErrors.scheduled_date = t.dateTimeMustBeFuture;
      }
    }

    // Validar enlace de Google Meet
    if (!formData.meet_link) {
      newErrors.meet_link = t.meetLinkRequired;
    } else if (!formData.meet_link.includes('meet.google.com')) {
      newErrors.meet_link = t.meetLinkMustBeValid;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleScheduleAppointment = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) {
        throw new Error(t.userNotAuthenticated);
      }

      // Si hay una cita vigente, cancelarla primero
      if (currentAppointment) {
        const { error: cancelError } = await supabase
          .from('appointment')
          .update({ status: 'cancelled' })
          .eq('id', currentAppointment.id);

        if (cancelError) {
          throw cancelError;
        }
      }

      // Crear nueva cita
      const { data, error } = await supabase
        .from('appointment')
        .insert({
          case_id: caseId,
          scheduled_date: formData.scheduled_date,
          meet_link: formData.meet_link,
          created_by: user.id,
          status: 'scheduled'
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Resetear formulario
      setFormData({
        scheduled_date: '',
        meet_link: ''
      });
      setErrors({});

      // Recargar citas
      await loadAppointments();

      // Notificar al componente padre
      if (onAppointmentScheduled) {
        onAppointmentScheduled(data);
      }

      alert(t.appointmentScheduledSuccessfully);
    } catch (error) {
      console.error('Error scheduling appointment:', error);
      alert(t.errorSchedulingAppointment + ': ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAppointment = async () => {
    if (!currentAppointment) return;

    /* if (!confirm(t.confirmCancelAppointment)) {
      return;
    } */

    setLoading(true);
    try {
      const { error } = await supabase
        .from('appointment')
        .update({ status: 'cancelled' })
        .eq('id', currentAppointment.id);

      if (error) {
        throw error;
      }

      await loadAppointments();
      
      if (onAppointmentScheduled) {
        onAppointmentScheduled(null);
      }

      alert(t.appointmentCancelledSuccessfully);
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      alert(t.errorCancellingAppointment + ': ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const locale = caseLanguage === 'english' ? 'en-US' : 'es-ES';
    return new Date(dateString).toLocaleString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return '#4caf50';
      case 'completed': return '#2196f3';
      case 'cancelled': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'scheduled': return t.appointmentStatusScheduled;
      case 'completed': return t.appointmentStatusCompleted;
      case 'cancelled': return t.appointmentStatusCancelled;
      default: return status;
    }
  };

  // Obtener la fecha mínima (ahora + 1 hora)
  const getMinDateTime = () => {
    const now = new Date();
    now.setHours(now.getHours() + 1);
    return now.toISOString().slice(0, 16);
  };

  const formatCreatedDate = (dateString) => {
    const locale = caseLanguage === 'english' ? 'en-US' : 'es-ES';
    return new Date(dateString).toLocaleDateString(locale);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CalendarIcon sx={{ color: officeColor }} />
        {t.manageAppointment}
      </DialogTitle>
      
      <DialogContent>
        {loadingHistory ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Cita Vigente */}
            {currentAppointment ? (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: officeColor }}>
                  {t.currentAppointment}
                </Typography>
                <Box sx={{ p: 2, border: '2px solid #4caf50', borderRadius: 1, backgroundColor: '#e8f5e8' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <ScheduleIcon sx={{ mr: 1, color: '#4caf50' }} />
                    <Typography variant="body1" fontWeight="bold">
                      {formatDate(currentAppointment.scheduled_date)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <VideoCallIcon sx={{ mr: 1, color: '#4caf50' }} />
                    <Typography 
                      variant="body2" 
                      component="a" 
                      href={currentAppointment.meet_link}
                      target="_blank"
                      sx={{ color: '#1976d2', textDecoration: 'none' }}
                    >
                      {t.meetingLink}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancelAppointment}
                    disabled={loading}
                    sx={{ color: '#f44336', borderColor: '#f44336' }}
                  >
                    {t.cancelAppointment}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Alert severity="info" sx={{ mb: 3 }}>
                {t.noActiveAppointments}
              </Alert>
            )}

            {/* Formulario para nueva cita */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ color: officeColor }}>
                {currentAppointment ? t.scheduleNewAppointment : t.scheduleAppointment}
              </Typography>
              
              {currentAppointment && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {t.newAppointmentWarning}
                </Alert>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label={t.dateAndTime}
                  type="datetime-local"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                  error={!!errors.scheduled_date}
                  helperText={errors.scheduled_date}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: getMinDateTime() }}
                  fullWidth
                />
                
                <TextField
                  label={t.googleMeetLink}
                  placeholder="https://meet.google.com/xxx-xxxx-xxx"
                  value={formData.meet_link}
                  onChange={(e) => setFormData({ ...formData, meet_link: e.target.value })}
                  error={!!errors.meet_link}
                  helperText={errors.meet_link}
                  fullWidth
                />
              </Box>
            </Box>

            {/* Historial de citas */}
            {appointmentHistory.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: officeColor }}>
                  {t.appointmentHistory} ({appointmentHistory.length})
                </Typography>
                <Box sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <List>
                    {appointmentHistory.map((appointment, index) => {
                      const isPast = new Date(appointment.scheduled_date) < new Date();
                      const isActive = appointment.status === 'scheduled' && !isPast;
                      
                      return (
                        <React.Fragment key={appointment.id}>
                          <ListItem sx={{ 
                            backgroundColor: isActive ? '#e8f5e8' : 'inherit',
                            borderLeft: isActive ? '4px solid #4caf50' : 'none'
                          }}>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <ScheduleIcon sx={{ 
                                    fontSize: 16, 
                                    color: isActive ? '#4caf50' : 'text.secondary' 
                                  }} />
                                  <Typography variant="body1" sx={{ 
                                    fontWeight: isActive ? 'bold' : 'normal' 
                                  }}>
                                    {formatDate(appointment.scheduled_date)}
                                  </Typography>
                                  <Chip
                                    label={getStatusLabel(appointment.status)}
                                    size="small"
                                    sx={{
                                      backgroundColor: getStatusColor(appointment.status),
                                      color: 'white',
                                      fontSize: '0.7rem'
                                    }}
                                  />
                                  {isActive && (
                                    <Chip
                                      label={t.active}
                                      size="small"
                                      sx={{
                                        backgroundColor: '#4caf50',
                                        color: 'white',
                                        fontSize: '0.7rem',
                                        fontWeight: 'bold'
                                      }}
                                    />
                                  )}
                                </Box>
                              }
                              secondary={
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <VideoCallIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                    <Typography 
                                      variant="body2" 
                                      component="a" 
                                      href={appointment.meet_link}
                                      target="_blank"
                                      sx={{ 
                                        color: '#1976d2', 
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' }
                                      }}
                                    >
                                      {t.meetingLink}
                                    </Typography>
                                  </Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {t.createdOn} {formatCreatedDate(appointment.created_at)}
                                  </Typography>
                                  {isPast && appointment.status === 'scheduled' && (
                                    <Typography variant="caption" sx={{ color: '#ff9800' }}>
                                      ⚠️ {t.expiredAppointment}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                          {index < appointmentHistory.length - 1 && <Divider />}
                        </React.Fragment>
                      );
                    })}
                  </List>
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined">
          {t.close}
        </Button>
        <Button
          onClick={handleScheduleAppointment}
          variant="contained"
          disabled={loading || loadingHistory}
          startIcon={loading ? <CircularProgress size={16} /> : <CalendarIcon />}
          sx={{
            backgroundColor: officeColor,
            '&:hover': { backgroundColor: officeColor, opacity: 0.9 }
          }}
        >
          {loading ? t.scheduling : t.scheduleAppointment}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AppointmentModal;